/**
 * Universal Search Bar - Amazon-Style with Accordions
 * Single search bar for companies and data sources with nested display
 */

import { useState, useEffect, useRef } from 'react';
import {
  Search,
  Building2,
  Database,
  Check,
  Loader2,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import { useDataSourceStore } from '../../store/dataSourceStore';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

interface DataSource {
  id: string;
  name: string;
  type: string;
  sourceType?: 'database' | 'file_upload' | 'api';
  config: {
    database: string;
    collection?: string;
  };
  availableModules?: string[];
  purpose?: string;
  isDefault: boolean;
  isActive: boolean;
}

interface Company {
  _id: string;
  name: string;
  slug: string;
  domain?: string;
  dataSources?: DataSource[];
}

const UniversalSearchBar = () => {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { selectedCompany, selectedDataSources, setSelectedCompany, setSelectedDataSources } =
    useDataSourceStore();

  // For backward compatibility, get first selected source
  const selectedDataSource = selectedDataSources[0] || null;
  const currentUser = useAuthStore(state => state.user);

  // Determine which module we're currently viewing
  const getCurrentModule = (): string | null => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/funnel')) return 'funnel';
    if (path.includes('/sessions')) return 'sessions';
    if (path.includes('/email-campaigns')) return 'email_campaigns';
    return null; // Admin pages or other pages - show all
  };

  const currentModule = getCurrentModule();

  // Check if user is super admin
  const isSuperAdmin =
    currentUser?.roles?.some((role: any) => {
      if (typeof role === 'string') {
        return role === 'super-admin' || role === 'super_admin';
      } else if (typeof role === 'object' && role?.slug) {
        return role.slug === 'super-admin' || role.slug === 'super_admin';
      }
      return false;
    }) || false;

  // Fetch ALL companies
  const { data: companiesData, isLoading: loadingCompanies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/api/v1/companies');
      return response.data.data;
    },
    enabled: true,
    staleTime: 30000,
  });

  const companies: Company[] = companiesData || [];

  // Auto-select first company and data source on load
  useEffect(() => {
    if (companies.length > 0 && !selectedCompany && selectedDataSources.length === 0) {
      const userCompany = isSuperAdmin
        ? companies[0]
        : companies.find(c => c._id === currentUser?.companyId);

      if (userCompany) {
        setSelectedCompany(userCompany);
        const defaultDS =
          userCompany.dataSources?.find(ds => ds.isDefault) || userCompany.dataSources?.[0];
        if (defaultDS) {
          setSelectedDataSources([defaultDS]);
        }
      }
    }
  }, [
    companies,
    selectedCompany,
    selectedDataSources.length,
    isSuperAdmin,
    currentUser,
    setSelectedCompany,
    setSelectedDataSources,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter companies based on query
  const filteredCompanies = query.trim()
    ? companies.filter(
        company =>
          company.name.toLowerCase().includes(query.toLowerCase()) ||
          company.slug.toLowerCase().includes(query.toLowerCase()) ||
          company.domain?.toLowerCase().includes(query.toLowerCase()) ||
          company.dataSources?.some(
            ds =>
              ds.name.toLowerCase().includes(query.toLowerCase()) ||
              ds.config.database.toLowerCase().includes(query.toLowerCase()) ||
              ds.config.collection?.toLowerCase().includes(query.toLowerCase())
          )
      )
    : companies;

  // For normal admins, only show their company
  const displayCompanies = isSuperAdmin
    ? filteredCompanies
    : filteredCompanies.filter(c => c._id === currentUser?.companyId);

  // Count total data sources
  const totalDataSources = displayCompanies.reduce(
    (acc, company) => acc + (company.dataSources?.length || 0),
    0
  );

  // Toggle company accordion
  const toggleCompany = (companyId: string) => {
    setExpandedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  // Handle company selection (for super admin)
  const selectCompany = (company: Company) => {
    setSelectedCompany(company);
    const defaultDS = company.dataSources?.find(ds => ds.isDefault) || company.dataSources?.[0];
    if (defaultDS) {
      setSelectedDataSources([defaultDS]);
      toast.success(`Switched to ${company.name} - ${defaultDS.name}`);
    } else {
      toast.success(`Switched to ${company.name}`);
    }
    setIsOpen(false);
    setQuery('');
  };

  // Handle data source toggle (multi-select)
  const toggleDataSource = (company: Company, dataSource: DataSource) => {
    if (isSuperAdmin) {
      setSelectedCompany(company);
    }

    // Check if already selected
    const isSelected = selectedDataSources.find(ds => ds.id === dataSource.id);

    if (isSelected) {
      // Remove from selection
      const newSelection = selectedDataSources.filter(ds => ds.id !== dataSource.id);
      setSelectedDataSources(newSelection);
      toast.success(`Removed: ${dataSource.name}`);
    } else {
      // Add the data source - each will be used for its designated modules
      setSelectedDataSources([...selectedDataSources, dataSource]);

      // Show which modules this source is available for
      const modulesList = dataSource.availableModules?.join(', ') || 'all modules';
      toast.success(`✅ ${dataSource.name} - Available for: ${modulesList}`);
    }
  };

  // Calculate overlapping modules
  const overlappingModules =
    selectedDataSources.length > 1
      ? (selectedDataSources[0]?.availableModules || []).filter(module =>
          selectedDataSources.every(ds => (ds.availableModules || []).includes(module))
        )
      : [];

  // Clear selection
  const clearSelection = () => {
    setSelectedCompany(null);
    setSelectedDataSources([]);
    toast('Selection cleared', { icon: 'ℹ️' });
  };

  // Current selection display text
  const currentSelection =
    selectedCompany && selectedDataSource
      ? `${selectedCompany.name} → ${selectedDataSource.name}`
      : selectedCompany
      ? selectedCompany.name
      : selectedDataSource
      ? selectedDataSource.name
      : isSuperAdmin
      ? 'Search companies and data sources...'
      : 'Search data sources...';

  return (
    <div className="relative w-full">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 lg:pl-4 flex items-center justify-center pointer-events-none">
          {loadingCompanies ? (
            <Loader2 className="w-4 h-4 lg:w-5 lg:h-5 text-secondary-400 animate-spin" />
          ) : (
            <Search className="w-4 h-4 lg:w-5 lg:h-5 text-secondary-400" />
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={currentSelection}
          className="w-full pl-10 lg:pl-12 pr-20 py-2 lg:py-3 rounded-lg lg:rounded-xl border-2 border-secondary-200 dark:border-secondary-700 
                   bg-secondary-50 dark:bg-secondary-900 text-secondary-900 dark:text-secondary-50 
                   focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent 
                   transition-all duration-200 ease-in-out text-sm lg:text-base"
        />

        {/* Right side icons */}
        <div className="absolute inset-y-0 right-0 pr-3 lg:pr-4 flex items-center gap-2">
          {(selectedCompany || selectedDataSource) && (
            <button
              onClick={clearSelection}
              className="p-1 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-full transition-colors"
              title="Clear selection"
            >
              <X className="w-4 h-4 text-secondary-500" />
            </button>
          )}
          {selectedCompany && <Building2 className="w-4 h-4 lg:w-5 lg:h-5 text-primary-500" />}
          {selectedDataSource && <Database className="w-4 h-4 lg:w-5 lg:h-5 text-green-500" />}
        </div>
      </div>

      {/* Dropdown Results - Accordion Style */}
      {isOpen && displayCompanies.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-secondary-800
                   rounded-lg lg:rounded-xl shadow-2xl border border-secondary-200 dark:border-secondary-700
                   max-h-[70vh] lg:max-h-96 overflow-y-auto z-50 animate-slide-down"
        >
          {/* Result Count Header */}
          <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900">
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              Found{' '}
              <span className="font-semibold text-primary-600 dark:text-primary-400">
                {displayCompanies.length}
              </span>{' '}
              {displayCompanies.length === 1 ? 'company' : 'companies'} with{' '}
              <span className="font-semibold text-green-600 dark:text-green-400">
                {totalDataSources}
              </span>{' '}
              {totalDataSources === 1 ? 'data source' : 'data sources'}
            </p>
          </div>

          {/* Companies List with Accordions */}
          <div className="divide-y divide-secondary-200 dark:divide-secondary-700">
            {displayCompanies.map(company => {
              const isExpanded = expandedCompanies.has(company._id);
              const isSelectedCompany = selectedCompany?._id === company._id;
              const hasDataSources = company.dataSources && company.dataSources.length > 0;

              return (
                <div key={company._id} className="group">
                  {/* Company Header */}
                  <div
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
                              ${
                                isSelectedCompany
                                  ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500'
                                  : 'hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                              }`}
                    onClick={() => {
                      if (hasDataSources) {
                        toggleCompany(company._id);
                      } else if (isSuperAdmin) {
                        selectCompany(company);
                      }
                    }}
                  >
                    {/* Expand/Collapse Icon */}
                    {hasDataSources ? (
                      isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-secondary-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-secondary-500" />
                      )
                    ) : (
                      <div className="w-4 h-4" />
                    )}

                    {/* Company Icon */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-white" />
                    </div>

                    {/* Company Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900 dark:text-secondary-50 truncate">
                        {company.name}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                        {company.domain || company.slug}
                        {hasDataSources &&
                          ` • ${company.dataSources!.length} data source${
                            company.dataSources!.length !== 1 ? 's' : ''
                          }`}
                      </p>
                    </div>

                    {/* Selected Checkmark */}
                    {isSelectedCompany && (
                      <Check className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    )}
                  </div>

                  {/* Data Sources (Nested) - MULTI-SELECT with Checkboxes */}
                  {isExpanded && hasDataSources && (
                    <div className="bg-secondary-50/50 dark:bg-secondary-900/50">
                      {company.dataSources!.map(dataSource => {
                        const isSelected = selectedDataSources.find(ds => ds.id === dataSource.id);

                        // Check if this source is compatible with current module
                        const isCompatibleWithModule =
                          !currentModule ||
                          (dataSource.availableModules || []).includes(currentModule);

                        return (
                          <div
                            key={dataSource.id}
                            className={`flex items-start gap-4 pl-6 pr-4 py-4 cursor-pointer transition-all
                                      ${
                                        isSelected
                                          ? 'bg-green-50 dark:bg-green-900/20'
                                          : 'hover:bg-secondary-100 dark:hover:bg-secondary-800'
                                      }`}
                            onClick={() => toggleDataSource(company, dataSource)}
                          >
                            {/* Checkbox - Better Positioned */}
                            <div className="flex-shrink-0 pt-0.5">
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                  isSelected
                                    ? 'bg-green-600 border-green-600'
                                    : 'bg-white dark:bg-secondary-700 border-secondary-400 dark:border-secondary-500'
                                }`}
                              >
                                {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                              </div>
                            </div>

                            {/* Data Source Icon */}
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                              <Database className="w-4 h-4 text-white" />
                            </div>

                            {/* Data Source Info */}
                            <div className="flex-1 min-w-0 space-y-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-medium text-secondary-900 dark:text-secondary-50">
                                  {dataSource.name}
                                </p>
                                {dataSource.isDefault && (
                                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                                    Default
                                  </span>
                                )}
                                {!isCompatibleWithModule && currentModule && (
                                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                                    ℹ️ For: {(dataSource.availableModules || []).join(', ')}
                                  </span>
                                )}
                              </div>

                              {/* Purpose */}
                              {dataSource.purpose && (
                                <p className="text-xs text-secondary-600 dark:text-secondary-400 italic">
                                  {dataSource.purpose}
                                </p>
                              )}

                              {/* Database Info */}
                              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                                {dataSource.sourceType === 'file_upload'
                                  ? '📁 '
                                  : dataSource.sourceType === 'api'
                                  ? '🔌 '
                                  : '🗄️ '}
                                {dataSource.config.database}
                                {dataSource.config.collection &&
                                  ` / ${dataSource.config.collection}`}
                              </p>

                              {/* Available Modules */}
                              {dataSource.availableModules &&
                                dataSource.availableModules.length > 0 && (
                                  <div>
                                    <p className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1.5">
                                      Available for:
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {dataSource.availableModules.map(module => {
                                        const isOverlapping =
                                          selectedDataSources.length > 1 &&
                                          isSelected &&
                                          overlappingModules.includes(module);
                                        return (
                                          <span
                                            key={module}
                                            className={`px-2 py-1 text-xs rounded-md font-medium ${
                                              isOverlapping
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 ring-1 ring-green-400 dark:ring-green-700'
                                                : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300'
                                            }`}
                                            title={
                                              isOverlapping
                                                ? 'Data will be combined from multiple sources'
                                                : module
                                            }
                                          >
                                            {module} {isOverlapping ? '🔗' : ''}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Multi-Select Summary */}
          {selectedDataSources.length > 1 && (
            <div className="px-4 py-3 border-t border-secondary-200 dark:border-secondary-700 bg-blue-50 dark:bg-blue-900/20">
              <p className="text-xs text-blue-900 dark:text-blue-200 font-medium mb-1">
                📊 {selectedDataSources.length} data sources selected
              </p>
              {overlappingModules.length > 0 && (
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  🔗 Combined data for: {overlappingModules.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Keyboard Hints */}
          <div className="px-4 py-2 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900">
            <p className="text-xs text-secondary-500 dark:text-secondary-400 text-center">
              ☑️ Check multiple data sources • 🔗 = Combined data • Press Esc to close
            </p>
          </div>
        </div>
      )}

      {/* No Results */}
      {isOpen && displayCompanies.length === 0 && !loadingCompanies && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-secondary-800
                   rounded-lg shadow-xl border border-secondary-200 dark:border-secondary-700 z-50"
        >
          <div className="px-4 py-8 text-center">
            <Search className="w-12 h-12 mx-auto mb-3 text-secondary-300 dark:text-secondary-600" />
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              No companies or data sources found
            </p>
            <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
              Try a different search term
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversalSearchBar;
